import React from "react";
import { Star, Quote, CheckCircle2, ExternalLink } from "lucide-react";
import { Meta, Schema } from "../components/SEO";
import { CtaStrip } from "../components/Sections";
import { useGoogleReviews, normalizeGoogleReview } from "../hooks/useGoogleReviews";
import { company } from "../data/company";

export default function ReviewsPage() {
  const live = useGoogleReviews();
  const liveConfigured = !!live?.configured;
  // Uitsluitend live Google reviews — geen curated/verzonnen reviews.
  const merged = (liveConfigured ? (live.reviews || []).map(normalizeGoogleReview) : [])
    .sort((a, b) => (b.rating || 5) - (a.rating || 5));

  // Live rating is de enige bron; geen hardcoded fallback-score.
  const heroRating = liveConfigured && live.rating ? live.rating : null;
  // Only show a real Google review count. Werkspot count is hidden until confirmed.
  const heroCount = liveConfigured && live.userRatingCount ? live.userRatingCount : null;

  const reviewsSchema = {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    name: "Koning Aannemersbedrijf",
    url: "https://koningaannemers.nl",
    ...(heroCount
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: heroRating,
            reviewCount: heroCount,
            bestRating: "5",
            worstRating: "1",
          },
        }
      : {}),
    review: merged.slice(0, 10).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: { "@type": "Rating", ratingValue: r.rating || 5, bestRating: "5" },
      reviewBody: r.text,
      ...(r.date ? { datePublished: r.date } : {}),
    })),
  };

  return (
    <>
      <Meta
        title="Reviews & klantbeoordelingen — Aannemer Maassluis | Koning"
        description="Lees echte klantbeoordelingen over Koning Aannemersbedrijf op Google en Werkspot. Ontdek waarom klanten in heel Zuid-Holland ons aanbevelen."
      />
      <Schema data={reviewsSchema} id="reviews-aggregate" />

      <section className="pt-24 pb-12 md:pt-32 md:pb-20 border-b border-koning-border" data-testid="reviews-hero">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="eyebrow mb-5">Klantbeoordelingen</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-white tracking-tight max-w-4xl">
            {heroRating ? <>{heroRating}/5 · <span className="italic text-koning-gold">{heroCount ? `${heroCount} Google-reviews` : "op Google"}</span></> : <>Onze klanten <span className="italic text-koning-gold">op Google & Werkspot</span></>}
          </h1>
          <p className="mt-6 text-koning-muted max-w-2xl leading-relaxed">
            Onze klanten zijn ons grootste visitekaartje. Alle reviews zijn afkomstig van Google en Werkspot — herleidbaar, eerlijk en zonder filter. Hieronder een selectie.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-koning-border">
            {[
              // Google — only surfaces a count when the live API returns one.
              { v: liveConfigured && live.rating ? live.rating.toFixed(1) : "—", l: liveConfigured && live.userRatingCount ? `Google · ${live.userRatingCount} reviews` : "Google · live", href: liveConfigured ? live.googleMapsUri : null },
              // Werkspot — badge + link; review count hidden until verified.
              { v: "W", l: "Werkspot Pro", href: company.werkspot?.profileUrl },
              { v: "VCA", l: "Gecertificeerde medewerkers" },
              { v: "15+", l: "Jaar praktijkervaring" },
            ].map((s) => (
              s.href ? (
                <a key={s.l} href={s.href} target="_blank" rel="noopener noreferrer" className="bg-koning-bg p-6 hover:bg-koning-surface transition-colors group" data-testid={`review-stat-${s.l.split(" ")[0].toLowerCase()}`}>
                  <div className="font-display text-koning-gold text-3xl md:text-4xl flex items-center gap-2">
                    {s.v}
                    <ExternalLink size={14} className="text-koning-muted group-hover:text-koning-gold transition-colors" />
                  </div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-koning-muted mt-2">{s.l}</div>
                </a>
              ) : (
                <div key={s.l} className="bg-koning-bg p-6">
                  <div className="font-display text-koning-gold text-3xl md:text-4xl">{s.v}</div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-koning-muted mt-2">{s.l}</div>
                </div>
              )
            ))}
          </div>

          {liveConfigured && (
            <div className="mt-6 inline-flex items-center gap-2 text-xs text-koning-gold" data-testid="reviews-live-indicator">
              <CheckCircle2 size={14} /> Live opgehaald van Google · dagelijks bijgewerkt
              {live.googleMapsUri && (
                <>
                  <span className="text-koning-muted">·</span>
                  <a href={live.googleMapsUri} target="_blank" rel="noopener noreferrer" data-testid="reviews-google-link" className="underline hover:text-koning-goldHover">
                    Bekijk op Google Maps
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 md:py-28" data-testid="reviews-grid">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {merged.map((r) => (
            <article key={r.id} className="bg-koning-surface border border-koning-border p-7 flex flex-col" data-testid={`reviews-page-review-${r.id}`}>
              <Quote className="text-koning-gold mb-5" size={28} />
              <p className="text-koning-text text-sm leading-relaxed flex-1">{r.text}</p>
              <div className="mt-6 pt-5 border-t border-koning-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {r.avatar && (
                    <img src={r.avatar} alt={r.name} className="w-8 h-8 rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                  )}
                  <div>
                    <div className="text-white text-sm font-medium">{r.name}</div>
                    <div className="text-koning-muted text-xs mt-0.5">{[r.city, r.project].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-0.5 justify-end">
                    {Array.from({ length: r.rating || 5 }).map((_, i) => (
                      <Star key={i} size={12} className="fill-koning-gold text-koning-gold" />
                    ))}
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-koning-muted mt-1">{r.source} · {r.date}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <CtaStrip title="Word onze volgende tevreden klant" subtitle="Binnen één werkdag persoonlijk contact." />
    </>
  );
}
